// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a floating legend can be moved and resized (owner, 2026-10-04).
 *
 *   ┌──────────────────── ✕ ┐  ← put it away in Tools → View
 *   │ ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬ │  ← press and drag anywhere on it to move it
 *   │ 0    5    10    15   ◢│  ← drag the corner to resize it
 *   └───────────────────────┘
 *
 * A press that moves a few pixels is a drag; one that does not is the
 * legend's own click (Tools → View for a colour bar, the enlarged picture
 * for a published one), so nothing it did before is lost. With the
 * keyboard, arrows move a focused legend and Ctrl+arrows resize it (not
 * Alt, which is the browser's Back on Alt+Left).
 * A double click on the corner puts it back where and as big as it was.
 * AYNI (2026-10-11): its ✕, or a drag that ends on the Tools panel or
 * the Tools button, puts it away in Tools → View (legendDock.ts).
 *
 * Main.ts rebuilds a colour bar on every palette change, so the placement
 * is not kept on the element: it is kept per globe (slot) and per browser,
 * and applied to whatever legend that globe shows next. The place is a
 * fraction of the globe's panel as it rests with nothing open (one globe:
 * the window), so it survives a window resize or a change of layout; the
 * size is in CSS pixels.
 *
 * **It stays in view (owner, 2026-10-11).** The side panels (Browse,
 * Tools, Analyze, the Studio, a lesson) open over the inline-end edge and
 * push the globe aside (`globePanelOffset.ts`). A legend is not pushed:
 * it keeps its place on the screen, and moves only as far as it must to
 * stay beside the panels and on the screen — the one in the usual corner
 * by the panels' whole width, one left in the middle not at all. While
 * the globe glides, the legend is placed every frame against the panel
 * sliding under it, so it never dips under a panel or off the edge. With
 * two or four globes the same holds for each globe's legend in its cell.
 */

import { t } from '../i18n'
import { PUSH_EVENT, PUSH_SETTLED_EVENT, pushEase, type PushEventDetail } from './globePanelOffset'

/** One globe's legend: where its top-left corner is, as fractions of its panel at rest, and its size (null = the stylesheet's). */
export interface LegendPlace {
  x: number
  y: number
  w: number | null
  h: number | null
}

/** A horizontal span, in viewport px. */
export interface Span {
  left: number
  right: number
}

const STORAGE_KEY = 'ayni-legend-place'
/** How far a press moves before it is a drag rather than a click. */
const DRAG_PX = 5
const MIN_W = 96
const MIN_H = 36
const KEY_STEP = 12
const KEY_STEP_BIG = 48
/** A move's frames go on this long past its end, through the grid's settling. */
const TAIL_MS = 150

let places: Record<string, LegendPlace> = load()
const bound = new WeakSet<HTMLElement>()
/** The legend each globe shows now, so a window resize or a push can keep it in view. */
const live = new Map<number, HTMLElement>()
/** What each legend was told once it stood still (main.ts: the date label keeps clear of it). */
const settledHooks = new WeakMap<HTMLElement, () => void>()
/** Where each legend's left edge is on screen, as last placed: the start of its next move. */
const lastLeft = new WeakMap<HTMLElement, number>()

function load(): Record<string, LegendPlace> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) as Record<string, Partial<LegendPlace>> : {}
    const out: Record<string, LegendPlace> = {}
    for (const [slot, p] of Object.entries(parsed)) {
      if (typeof p?.x !== 'number' || typeof p.y !== 'number' || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue
      const size = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null)
      out[slot] = { x: p.x, y: p.y, w: size(p.w), h: size(p.h) }
    }
    return out
  } catch {
    // storage refused or garbled: every legend in its usual corner
    return {}
  }
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(places))
  } catch {
    // storage refused: the placement is forgotten with the page
  }
}

/** The placement a globe's legend has, if it was ever moved or resized. */
export function legendPlace(slot: number): LegendPlace | null {
  return places[String(slot)] ?? null
}

/** Keep a box of this size inside its panel; fractions in, pixels out. */
export function clampPlace(place: LegendPlace, box: { w: number; h: number }, panel: { w: number; h: number }): { left: number; top: number; w: number | null; h: number | null } {
  const w = place.w === null ? null : Math.max(MIN_W, Math.min(place.w, panel.w))
  const h = place.h === null ? null : Math.max(MIN_H, Math.min(place.h, panel.h))
  const bw = w ?? box.w
  const bh = h ?? box.h
  const left = Math.round(Math.min(Math.max(0, place.x * panel.w), Math.max(0, panel.w - bw)))
  const top = Math.round(Math.min(Math.max(0, place.y * panel.h), Math.max(0, panel.h - bh)))
  return { left, top, w, h }
}

/**
 * The part of a globe's resting span (`home`) a legend may stand in while
 * the side panels take `inset` px off the inline-end edge of a window
 * `viewportWidth` wide: on the screen, and beside the panels.
 */
export function freeSpan(home: Span, inset: number, viewportWidth: number, rtl = false): Span {
  const left = Math.max(home.left, rtl ? inset : 0)
  const right = Math.min(home.right, rtl ? viewportWidth : viewportWidth - inset)
  return { left, right: Math.max(left, right) }
}

/** Where a legend `width` wide whose place starts at `homeLeft` stands within `span`: its place, or the nearest it fits. */
export function clampX(homeLeft: number, width: number, span: Span): number {
  return Math.min(Math.max(homeLeft, span.left), Math.max(span.left, span.right - width))
}

/** The globe's panel a legend floats on; null once it is adopted elsewhere (the phone's chip, the dock). */
function panelOf(el: HTMLElement): HTMLElement | null {
  const panel = el.offsetParent as HTMLElement | null
  return panel && panel.classList.contains('map-viewport') ? panel : null
}

const rtl = (): boolean => document.documentElement.dir === 'rtl'

/** How far one globe's panel is widened under the panels right now (`globePanelOffset.ts` extent); 0 with several globes. */
function widening(panel: HTMLElement): number {
  const grid = panel.parentElement
  if (!grid || grid.children.length > 1) return 0
  return Math.max(0, -(parseFloat(grid.style.insetInlineStart) || 0))
}

/** The panel's width as it rests with nothing open: the window's for one globe, its cell's otherwise. */
function homeWidth(panel: HTMLElement): number {
  return Math.max(0, panel.clientWidth - widening(panel))
}

/**
 * The panel's span as it rests with nothing open: the stage under the
 * grid for one globe (the grid itself is widened and slid under the
 * panels), the cell for two or four. Without layout (tests), from the
 * panel's size.
 */
function homeSpan(panel: HTMLElement): Span {
  const grid = panel.parentElement
  const stage = grid && grid.children.length <= 1 ? grid.parentElement : null
  const r = (stage ?? panel).getBoundingClientRect()
  if (r.width > 0) return { left: r.left, right: r.right }
  return { left: 0, right: homeWidth(panel) }
}

/** The whole inset the side panels take, px (`--panel-inset`, globePanelOffset.ts). */
function currentInset(): number {
  return parseFloat(document.documentElement.style.getPropertyValue('--panel-inset')) || 0
}

/** How far `el` is shifted along x to stay in view (its `translate`; by name, which every DOM keeps). */
function shiftOf(el: HTMLElement): number {
  const x = parseFloat(el.style.getPropertyValue('translate'))
  return Number.isFinite(x) ? x : 0
}

function setShift(el: HTMLElement, x: number): void {
  if (Math.abs(x) < 0.5) el.style.removeProperty('translate')
  else el.style.setProperty('translate', `${Math.round(x)}px 0`)
}

/** Where the legend's place puts its left edge on screen, before it is kept in view. */
function homeLeft(el: HTMLElement, panel: HTMLElement, place: LegendPlace | null, width: number, home: Span): number {
  const homeW = home.right - home.left
  if (place) {
    const x = Math.min(Math.max(0, place.x * homeW), Math.max(0, homeW - width))
    return home.left + x
  }
  // The stylesheet's corner, at the inline-end edge.
  const margin = parseFloat(getComputedStyle(el).insetInlineEnd) || 0
  return rtl() ? home.left + margin : home.right - margin - width
}

/** Where the legend's left edge belongs on screen now: its place, kept beside the panels and on the screen. */
function targetLeft(el: HTMLElement, panel: HTMLElement, place: LegendPlace | null, width: number): number {
  const home = homeSpan(panel)
  return clampX(homeLeft(el, panel, place, width, home), width, freeSpan(home, currentInset(), window.innerWidth, rtl()))
}

/** Put the legend at its target at once, from wherever its layout has it. */
function settleShift(el: HTMLElement, place: LegendPlace | null): void {
  const panel = panelOf(el)
  if (!panel || !el.isConnected) return
  const rect = el.getBoundingClientRect()
  if (!rect.width) {
    setShift(el, 0)
    return
  }
  const target = targetLeft(el, panel, place, rect.width)
  setShift(el, target - (rect.left - shiftOf(el)))
  lastLeft.set(el, target)
}

/** Write a placement onto the element (or clear it, back to the stylesheet's corner), and keep it in view. */
function apply(el: HTMLElement, place: LegendPlace | null): void {
  const panel = panelOf(el)
  if (!panel) return
  if (!place) {
    el.classList.remove('legend-placed', 'legend-sized')
    for (const prop of ['left', 'top', 'bottom', 'inset-inline-end', 'inline-size', 'block-size', '--legend-scale']) el.style.removeProperty(prop)
    settleShift(el, null)
    return
  }
  // Sized first, so the clamp measures the box it will be.
  if (place.w !== null) {
    el.style.setProperty('inline-size', `${Math.round(place.w)}px`)
    el.style.setProperty('block-size', `${Math.round(place.h ?? el.offsetHeight)}px`)
  }
  el.classList.add('legend-placed')
  el.classList.toggle('legend-sized', place.w !== null)
  const at = clampPlace(place, { w: el.offsetWidth, h: el.offsetHeight }, { w: homeWidth(panel), h: panel.clientHeight })
  // Across: pixels from the panel's resting edge, past whatever it is
  // widened by under the panels, so the place is the same point of the
  // screen whatever is open. Down: percent, so it stays on a resize.
  el.style.setProperty('left', `${(rtl() ? 0 : widening(panel)) + at.left}px`)
  el.style.setProperty('top', `${((at.top / panel.clientHeight) * 100).toFixed(3)}%`)
  el.style.setProperty('bottom', 'auto')
  el.style.setProperty('inset-inline-end', 'auto')
  if (at.w !== null) {
    el.style.setProperty('inline-size', `${at.w}px`)
    el.style.setProperty('block-size', `${at.h}px`)
  }
  el.style.setProperty('--legend-scale', String(legendScale(el, at.w)))
  settleShift(el, place)
}

/** Text grows with a widened legend, so a big one on a projector is read from the back. */
function legendScale(el: HTMLElement, w: number | null): number {
  if (w === null) return 1
  const base = el.classList.contains('panel-legend') ? 180 : 224
  return Math.min(2, Math.max(1, w / base))
}

/**
 * The place, as a fraction of the panel's resting width, of a legend
 * `width` wide seen at panel-x `left` (its layout plus its shift): where it
 * is seen, kept in the free span, so one let go half under a panel is
 * remembered where it is shown, beside the panel, and stays there when the
 * panel closes.
 */
function homeFraction(panel: HTMLElement, left: number, width: number): number {
  const homeW = homeWidth(panel)
  if (!homeW) return 0
  const home = homeSpan(panel)
  const seen = home.left + left - (rtl() ? 0 : widening(panel))
  const kept = clampX(seen, width, freeSpan(home, currentInset(), window.innerWidth, rtl()))
  return (kept - home.left) / homeW
}

/** Where the element is now, as a placement of its panel. */
function placeOf(el: HTMLElement, sized: boolean): LegendPlace | null {
  const panel = panelOf(el)
  if (!panel || !panel.clientWidth || !panel.clientHeight) return null
  return {
    x: homeFraction(panel, el.offsetLeft + shiftOf(el), el.offsetWidth),
    y: el.offsetTop / panel.clientHeight,
    w: sized ? el.offsetWidth : null,
    h: sized ? el.offsetHeight : null,
  }
}

function store(slot: number, place: LegendPlace | null): void {
  if (place) places[String(slot)] = place
  else delete places[String(slot)]
  save()
}

/** Forget every placement (tests; a fresh start). */
export function resetLegendPlaces(): void {
  places = {}
  save()
  for (const el of live.values()) apply(el, null)
}

/** The legends on their globes now, with their places. */
function onStage(): Array<{ slot: number; el: HTMLElement; panel: HTMLElement }> {
  const out: Array<{ slot: number; el: HTMLElement; panel: HTMLElement }> = []
  for (const [slot, el] of live) {
    const panel = el.isConnected ? panelOf(el) : null
    if (panel) out.push({ slot, el, panel })
  }
  return out
}

/** A move under way: when it began, how long it takes, and where each legend set out from. */
let move: { since: number; duration: number; starts: Map<HTMLElement, number> } | null = null
let frameHandle = 0

/**
 * One frame of a move: each legend glides from where it set out to where
 * it belongs, on the move's own curve, placed against the panel sliding
 * under it. Carries on a little past the end, through the grid's settling.
 */
function frame(now: number): void {
  frameHandle = 0
  if (!move) return
  const t = move.duration ? Math.min(1, (now - move.since) / move.duration) : 1
  const e = pushEase(t)
  for (const { slot, el, panel } of onStage()) {
    if (el.classList.contains('legend-dragging') || el.classList.contains('legend-resizing')) continue
    const rect = el.getBoundingClientRect()
    if (!rect.width) continue
    const target = targetLeft(el, panel, legendPlace(slot), rect.width)
    const start = move.starts.get(el) ?? target
    const x = start + (target - start) * e
    setShift(el, x - (rect.left - shiftOf(el)))
    lastLeft.set(el, x)
  }
  if (t < 1 || now < move.since + move.duration + TAIL_MS) {
    frameHandle = requestAnimationFrame(frame)
    return
  }
  move = null
  for (const { el } of onStage()) settledHooks.get(el)?.()
}

/** The panels' inset changed, or the globe is on its way: every legend sets out from where it is. */
function onPush(e: Event): void {
  const { duration } = (e as CustomEvent<PushEventDetail>).detail
  const starts = new Map<HTMLElement, number>()
  for (const { el } of onStage()) {
    const known = lastLeft.get(el)
    starts.set(el, known ?? el.getBoundingClientRect().left - shiftOf(el))
  }
  move = { since: performance.now(), duration, starts }
  if (!frameHandle) frameHandle = requestAnimationFrame(frame)
}

/** The grid stands still again: each legend exactly where it belongs. */
function onSettled(): void {
  if (move) return
  for (const { slot, el } of onStage()) settleShift(el, legendPlace(slot))
}

let hooked = false
function hookWindow(): void {
  if (hooked || typeof window === 'undefined') return
  hooked = true
  window.addEventListener('resize', () => {
    for (const [slot, el] of live) if (el.isConnected) apply(el, legendPlace(slot))
  })
  window.addEventListener(PUSH_EVENT, onPush)
  window.addEventListener(PUSH_SETTLED_EVENT, onSettled)
}

export interface MovableLegendOptions {
  /** It moved or changed size: whatever keeps clear of it should measure again. */
  onSettled?(): void
  /** AYNI (2026-10-11): its ✕ was pressed; without it, no ✕. */
  onClose?(): void
  /** Where it can be let go to be put away (legendDock.ts): the Tools panel, the Tools button. */
  dropTargets?(): HTMLElement[]
  /** It is carried over one of them, or none. */
  onDropHover?(target: HTMLElement | null): void
  /** It was let go over one of them. */
  onDrop?(target: HTMLElement): void
}

/** Store where a legend let go at `rect` (client px) sits on its globe's `panel`, keeping its size. */
export function placeLegendAt(slot: number, panel: HTMLElement, rect: DOMRect): void {
  const box = panel.getBoundingClientRect()
  if (!panel.clientWidth || !panel.clientHeight) return
  const was = legendPlace(slot)
  // Where it was let go on the screen is its place: the panel's resting span, not its widened one.
  const home = homeSpan(panel)
  const homeW = home.right - home.left
  store(slot, {
    x: homeW ? (rect.left - home.left) / homeW : 0,
    y: (rect.top - box.top - panel.clientTop) / panel.clientHeight,
    w: was?.w ?? null,
    h: was?.h ?? null,
  })
}

/**
 * Make a globe's floating legend movable and resizable, and put it where
 * that globe's legend was last left. Safe to call on every refresh: an
 * element is wired once and only re-placed after that.
 */
export function makeLegendMovable(el: HTMLElement, slot: number, opts: MovableLegendOptions = {}): void {
  live.set(slot, el)
  if (opts.onSettled) settledHooks.set(el, opts.onSettled)
  hookWindow()
  if (bound.has(el)) {
    apply(el, legendPlace(slot))
    return
  }
  bound.add(el)
  el.classList.add('legend-movable')
  el.title = el.title ? `${el.title} · ${t('legend.moveHint')}` : t('legend.moveHint')

  const corner = document.createElement('span')
  corner.className = 'legend-resize'
  corner.setAttribute('aria-hidden', 'true')
  el.appendChild(corner)

  // AYNI (owner, 2026-10-11): ✕ puts it away in Tools → View (legendDock.ts).
  // A span, as the legend itself may be a button.
  const close = opts.onClose ? document.createElement('span') : null
  if (close) {
    close.className = 'legend-close'
    close.setAttribute('role', 'button')
    close.tabIndex = 0
    close.setAttribute('aria-label', t('legend.close'))
    close.title = t('legend.close')
    close.textContent = '✕'
    close.addEventListener('pointerdown', (e) => e.stopPropagation())
    close.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()
      opts.onClose?.()
    })
    close.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.stopPropagation()
      e.preventDefault()
      opts.onClose?.()
    })
    el.appendChild(close)
  }

  /** Set when a press became a drag, so the click that follows is not the legend's click. */
  let swallowClick = false
  el.addEventListener('click', (e) => {
    // The corner is for resizing: a click there (half of a double click)
    // must not open Tools or the picture.
    if (!swallowClick && e.target !== corner) return
    swallowClick = false
    e.stopImmediatePropagation()
    e.preventDefault()
  }, true)

  const settle = (sized: boolean) => {
    store(slot, placeOf(el, sized || legendPlace(slot)?.w != null))
    apply(el, legendPlace(slot))
    opts.onSettled?.()
  }

  el.addEventListener('pointerdown', (e) => {
    if (e.button > 0) return
    const panel = panelOf(el)
    if (!panel) return
    const resizing = e.target === corner
    // The globe under it must not take the press for a spin.
    e.stopPropagation()
    if (resizing) e.preventDefault()
    const start = { x: e.clientX, y: e.clientY, left: el.offsetLeft, top: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight }
    let dragging = false
    /** The drop target under the pointer, while a move (not a resize) is carried over one. */
    let over: HTMLElement | null = null
    const hover = (x: number, y: number) => {
      const next = resizing ? null : opts.dropTargets?.().find((d) => {
        const r = d.getBoundingClientRect()
        return r.width > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
      }) ?? null
      if (next === over) return
      over = next
      el.classList.toggle('legend-over-dock', !!over)
      opts.onDropHover?.(over)
    }
    const move = (m: PointerEvent) => {
      if (m.pointerId !== e.pointerId) return
      const dx = m.clientX - start.x
      const dy = m.clientY - start.y
      if (dragging) hover(m.clientX, m.clientY)
      if (!dragging) {
        if (Math.hypot(dx, dy) < DRAG_PX) return
        dragging = true
        el.classList.add(resizing ? 'legend-resizing' : 'legend-dragging')
        // From here it is placed by pixels; the stylesheet's corner no longer applies,
        // and the shift that kept it in view is folded into its place.
        start.left += shiftOf(el)
        setShift(el, 0)
        el.classList.add('legend-placed')
        el.style.setProperty('bottom', 'auto')
        el.style.setProperty('inset-inline-end', 'auto')
        el.style.setProperty('left', `${start.left}px`)
        el.style.setProperty('top', `${start.top}px`)
      }
      m.preventDefault()
      if (resizing) {
        const w = Math.max(MIN_W, Math.min(start.w + dx, panel.clientWidth - start.left))
        const h = Math.max(MIN_H, Math.min(start.h + dy, panel.clientHeight - start.top))
        el.classList.add('legend-sized')
        el.style.setProperty('inline-size', `${Math.round(w)}px`)
        el.style.setProperty('block-size', `${Math.round(h)}px`)
        el.style.setProperty('--legend-scale', String(legendScale(el, w)))
      } else {
        const left = Math.min(Math.max(0, start.left + dx), panel.clientWidth - el.offsetWidth)
        const top = Math.min(Math.max(0, start.top + dy), panel.clientHeight - el.offsetHeight)
        el.style.setProperty('left', `${Math.round(left)}px`)
        el.style.setProperty('top', `${Math.round(top)}px`)
      }
    }
    const end = (u: PointerEvent) => {
      if (u.pointerId !== e.pointerId) return
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', end)
      document.removeEventListener('pointercancel', end)
      if (!dragging) return
      el.classList.remove('legend-dragging', 'legend-resizing')
      swallowClick = true
      // A drag that ends off the element sends no click to swallow.
      setTimeout(() => { swallowClick = false }, 0)
      // Let go over the Tools panel or its button: put away, from where it is now. Its place stays the one it had.
      const target = u.type === 'pointerup' ? over : null
      if (over) {
        over = null
        el.classList.remove('legend-over-dock')
        opts.onDropHover?.(null)
      }
      if (target) {
        opts.onDrop?.(target)
        if (el.isConnected) apply(el, legendPlace(slot))
        return
      }
      settle(resizing)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', end)
    document.addEventListener('pointercancel', end)
  })

  // Double click on the corner: back to the usual corner and size.
  corner.addEventListener('dblclick', (e) => {
    e.stopPropagation()
    e.preventDefault()
    store(slot, null)
    apply(el, null)
    opts.onSettled?.()
  })

  // Arrows move it; Ctrl (or ⌘) + arrows resize it; Shift for bigger steps.
  el.addEventListener('keydown', (e) => {
    const step = e.shiftKey ? KEY_STEP_BIG : KEY_STEP
    const d = ({ ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] } as Record<string, number[]>)[e.key]
    const panel = panelOf(el)
    if (!d || !panel) return
    e.preventDefault()
    e.stopPropagation()
    const now = placeOf(el, true)!
    const resized = e.ctrlKey || e.metaKey
    // From where it is seen (its layout plus the shift that keeps it in view).
    const next: LegendPlace = resized
      ? { x: now.x, y: now.y, w: Math.max(MIN_W, el.offsetWidth + d[0]), h: Math.max(MIN_H, el.offsetHeight + d[1]) }
      : { x: homeFraction(panel, el.offsetLeft + shiftOf(el) + d[0], el.offsetWidth), y: (el.offsetTop + d[1]) / panel.clientHeight, w: legendPlace(slot)?.w ?? null, h: legendPlace(slot)?.h ?? null }
    store(slot, next)
    apply(el, next)
    opts.onSettled?.()
  })

  apply(el, legendPlace(slot))
}
